import { create } from '@bufbuild/protobuf'
import { useQuery } from '@connectrpc/connect-query'
import { getGenerationDetail } from '@ebalance/proto/bffuser/v1/balance_service-BalanceGenerationService_connectquery'
import { GetGenerationDetailRequestSchema } from '@ebalance/proto/bffuser/v1/balance_service_pb'
import { keepPreviousData } from '@tanstack/react-query'

import { isRegionLoading } from '~/hooks/isRegionLoading'
import { toProtoDate } from '~/models/dateTime'
import type { GenerationDetailData } from '~/pages/BalanceGenerationDetail/models/convertGenerationDetailResponse'
import { convertGenerationDetailResponse } from '~/pages/BalanceGenerationDetail/models/convertGenerationDetailResponse'

// #2000: 発電所別画面 (/balance/generation-detail) の実 BFF 接続。#1996 (発電
// ダッシュボード) の接続パターン (useQuery + 変換関数を hook 外に切り出す) を踏襲。
export function useGenerationDetail(
  displayDate: Date,
  bgCode: string,
): {
  data: GenerationDetailData | undefined
  /**
   * 領域を影 + スピナーで覆うか (UI 辞書 components/region-loading.md)。 初回の取得中と、
   * 表示日・BG を変えた後の取得中 (前の結果を placeholder として残している間) に true。
   * 5 秒ごとの自動更新では立てない。
   */
  isRegionLoading: boolean
  isError: boolean
} {
  // bgCode が空 (= URL に ?bg= が無い) の場合はクエリを撃たない。backend の
  // ParseBgCode は空文字 / OCCTO 5 桁形式でない値を CodeInvalidArgument で弾くため、
  // enabled で撃たないと refetchInterval が 5 秒ごとに失敗リクエストを繰り返す。
  const query = useQuery(
    getGenerationDetail,
    create(GetGenerationDetailRequestSchema, {
      displayDate: toProtoDate(displayDate),
      generationBgCode: bgCode,
    }),
    {
      refetchInterval: 5_000,
      enabled: bgCode !== '',
      // 表示日・BG を変えた直後も前の結果 (とフィルタカード) を残す。
      placeholderData: keepPreviousData,
      /*
       * fail-safe: global QueryClient の `throwOnError: true`
       * (`providers/ApiClientProvider.tsx`) を本 query だけ `false` に上書きする。
       * これが無いと ConnectError が render 中に throw され、 router の
       * `errorElement` (= 本番 `ErrorLayout` の全画面エラー) へ飛ぶため、
       * **呼び出し元の `isError` 分岐に到達しない** (= 画面内のエラーバナーが
       * dead code になる)。 spec `doji-hatsu.md` AC-BDH-026 は画面上部の
       * エラーバナーを要求しているので、 ここで購読側へ渡す。
       * 既存の同型: `hooks/useCurrentSession.ts:37` (「必須」と明記) ほか 10 本以上。
       */
      throwOnError: false,
    },
  )

  return {
    data: query.data
      ? convertGenerationDetailResponse(query.data, bgCode)
      : undefined,
    isRegionLoading: isRegionLoading(query),
    isError: query.isError,
  }
}
